import { PayPalHookProps } from '@msc-b2c/interfaces/types';
import {
  closeLoader,
  errorModalOpen,
  getCurrencyCode,
  myAreaFinalizeBookingPayment,
  PaymentContext,
  useScript,
} from '@msc-b2c/shared-utils';
import { useContext, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

export const usePaypal = ({
  merchantIdBooking,
  datatransPayPalUrl,
  transactionId,
  amount,
  paymentType,
}: PayPalHookProps) => {
  const [isLoaded, hasError] = useScript({ src: datatransPayPalUrl });
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [success, setSuccess] = useState<boolean | undefined>(undefined);

  // Typed to HTMLDivElement to avoid 'never' type errors
  const payPal = useRef<HTMLDivElement>(null);
  const paypalButton = window?.PayPalButton;

  const { onFinalizePayment } = useContext(PaymentContext);
  const onFinalizePaymentRef = useRef(onFinalizePayment);
  onFinalizePaymentRef.current = onFinalizePayment;
  const dispatch = useDispatch();

  const cleanupPaypalButton = () => {
    if (paypalButton) {
      try {
        paypalButton.destroy();
      } catch (err) {
        console.log('Unmount error', err);
      }
    }
  };

  useEffect(() => {
    // Check for payPal.current and necessary dependencies
    if (isLoaded && !hasError && paypalButton && payPal.current && transactionId && !success) {
      const paypalConfig = {
        paypaloptions: {
          button: {
            layout: 'vertical',
            color: 'gold',
            shape: 'rect',
            label: 'paypal',
          },
        },
        transaction: {
          refno: paymentType + '_' + new Date().getTime(),
          authenticationOnly: true,
          returnAddress: false,
        },
      };

      const handleInit = () => {
        const container = payPal.current;
        if (container) {
          // Prevent button doubling by clearing the container before creation
          while (container.firstChild) {
            container.removeChild(container.firstChild);
          }
          paypalButton.create(container, paypalConfig);
        }
      };

      const handleError = (err: unknown) => {
        console.log('ERROR on paypal btn', err);
      };

      const handleAuth = (response: { status: string; transactionId: string }) => {
        console.log('response: ', response);

        if (response?.status === 'success') {
          onFinalizePaymentRef.current(
            { transactionId: response.transactionId, type: paymentType },
            (res: any) => {
              if (res) {
                dispatch(myAreaFinalizeBookingPayment(res));
                closeLoader('.loader');
                setSuccess(true);
                console.log('Finalize payment response:', res);
              } else {
                setSuccess(false);
                dispatch(errorModalOpen(true));
              }
            },
            (error: any) => {
              console.log('Finalize payment response - Error', error);
              setSuccess(false);
              dispatch(errorModalOpen(true));
            },
            () => {
              setPaymentLoading(false);
            }
          );
        }
      };

      paypalButton.on('init', handleInit);
      paypalButton.on('error', handleError);
      paypalButton.on('authorization', handleAuth);

      paypalButton.init({
        merchantId: merchantIdBooking,
        reqType: 'CAA',
        currencyCode: getCurrencyCode(),
        amount,
        createAlias: false,
        PAP: { createOrder: true },
      });

      return () => {
        if (paypalButton) {
          paypalButton.off('init', handleInit);
          paypalButton.off('error', handleError);
          paypalButton.off('authorization', handleAuth);
          cleanupPaypalButton();
        }
      };
    }
  }, [
    isLoaded,
    hasError,
    paypalButton,
    transactionId,
    success,
    paymentType,
    amount,
    merchantIdBooking,
    dispatch,
  ]);

  return { payPal, paymentLoading };
};
